import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { FadeUp, SectionTag } from "../components/motion";
import { SOLUTIONS } from "../data/content";
import useSEO from "../hooks/useSEO";

export default function SolutionDetail() {
  const { slug } = useParams();
  const solution = SOLUTIONS.find((s) => s.slug === slug);
  useSEO(
    solution ? `${solution.title} in Patna | SATI Global Systems (SGSNet)` : "Solutions | SGSNet Patna",
    solution ? `${solution.short} SGSNet Patna — MSME registered & GeM authorized. Get technical consultation from SATI Global Systems.` : ""
  );

  if (!solution) {
    return (
      <main className="pt-32 pb-24 text-center" data-testid="solution-not-found">
        <h1 className="font-heading text-3xl font-bold text-slate-900">Solution not found</h1>
        <Link to="/solutions" className="mt-4 inline-block text-blue-600 text-sm">← All solutions</Link>
      </main>
    );
  }

  const others = SOLUTIONS.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <main data-testid={`solution-detail-${slug}`} className="pb-24">
      <section className="relative pt-16 sm:pt-24 pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <img src={solution.image} alt={solution.title} className="w-full h-full object-cover spotlight-img opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#FFFFFF]/70 via-[#FFFFFF]/85 to-[#FFFFFF]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <Link to="/solutions" data-testid="solution-back-link"
              className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-slate-500 hover:text-blue-600 transition-colors">
              <ArrowLeft size={13} /> Solutions
            </Link>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="mt-8 flex items-start gap-6">
              <span className="hidden sm:block font-heading font-black text-7xl text-outline leading-none">{solution.num}</span>
              <div>
                <SectionTag>Solution {solution.num}</SectionTag>
                <h1 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight max-w-3xl">
                  {solution.title}
                </h1>
                <p className="mt-5 max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed">{solution.overview}</p>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 mt-14 grid lg:grid-cols-2 gap-10">
        <div>
          <FadeUp><h2 className="font-heading text-2xl font-bold text-slate-900">Capabilities</h2></FadeUp>
          <div className="mt-6 space-y-3">
            {solution.capabilities.map((c, i) => (
              <FadeUp key={c} delay={i * 0.05}>
                <div className="flex items-center gap-3 p-4 rounded-lg bg-panel border border-slate-200" data-testid={`capability-${i}`}>
                  <CheckCircle2 size={16} className="text-blue-600 shrink-0" />
                  <span className="text-sm text-slate-700">{c}</span>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
        <div>
          <FadeUp><h2 className="font-heading text-2xl font-bold text-slate-900">What we deliver</h2></FadeUp>
          <div className="mt-6 grid gap-3">
            {solution.deliverables.map((d, i) => (
              <FadeUp key={d} delay={i * 0.05}>
                <div className="p-4 rounded-lg border border-blue-600/20 bg-blue-50/20" data-testid={`deliverable-${i}`}>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-blue-600">Deliverable {String(i + 1).padStart(2, "0")}</span>
                  <p className="mt-1 text-sm text-slate-700 font-medium">{d}</p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.2}>
            <Link to="/contact" data-testid="solution-cta-consultation"
              className="group mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-blue-600 text-[#FFFFFF] font-semibold text-sm hover:bg-blue-700 transition-colors">
              Get Technical Consultation
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </FadeUp>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 mt-20">
        <FadeUp><h2 className="font-heading text-xl font-bold text-slate-900">Related solutions</h2></FadeUp>
        <div className="mt-6 grid sm:grid-cols-3 gap-4">
          {others.map((s) => (
            <Link key={s.slug} to={`/solutions/${s.slug}`} data-testid={`related-solution-${s.slug}`}
              className="group bg-panel border border-slate-200 rounded-xl p-5 hover:border-blue-600/40 transition-colors">
              <s.icon size={20} className="text-blue-600" strokeWidth={1.6} />
              <h3 className="mt-3 font-heading font-semibold text-slate-900 text-[15px] group-hover:text-blue-700 transition-colors">{s.title}</h3>
            </Link>
          ))}
        </div>
      </section>
    </main>
  );
}
